import React, { useRef, useState, useEffect } from 'react';
import Webcam from 'react-webcam';
import * as faceapi from 'face-api.js';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

function SelfieCapturePage() {
  const webcamRef = useRef(null);
  const [mensaje, setMensaje] = useState('');
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [capturada, setCapturada] = useState(false);
  const [isDevelopment, setIsDevelopment] = useState(window.location.hostname === 'localhost');
  const [tiempoTranscurrido, setTiempoTranscurrido] = useState(0);
  const [mostrarBotonManual, setMostrarBotonManual] = useState(false);
  const [preparandoCaptura, setPreparandoCaptura] = useState(false);
  const [rostroEnOvalo, setRostroEnOvalo] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { cedula: cedulaParam } = useParams();

  // Obtener cédula, documentoId y firmanteIndex desde state o params
  const cedula = location.state?.cedula || cedulaParam;
  const documentoId = location.state?.documentoId;
  const firmanteIndex = location.state?.firmanteIndex;

  // 🔍 DIAGNÓSTICO AL CARGAR LA PÁGINA (SALVAVIDAS #1)
  useEffect(() => {
    console.log('📸 ========== SELFIE PAGE CARGADA ==========');
    console.log('📋 Cédula recibida:', cedula);
    console.log('📄 Documento ID:', documentoId);
    console.log('👥 Firmante Index:', firmanteIndex);
    console.log('🌐 Location state completo:', location.state);
    console.log('🔗 Params de URL:', cedulaParam);
    console.log('==========================================');
    
    // Verificar que tenemos los datos necesarios
    if (!cedula) {
      console.error('❌ ERROR CRÍTICO: No hay cédula');
      alert('⚠️ Error: No se recibió el número de cédula. Serás redirigido al inicio.');
      setTimeout(() => {
        navigate('/patient-access');
      }, 2000);
      return;
    }

    if (!documentoId) {
      console.error('❌ ERROR CRÍTICO: No hay documentoId en el state');
      alert('⚠️ Error: No se recibió el documento correctamente. Serás redirigido al inicio.');
      setTimeout(() => {
        navigate('/patient-access');
      }, 2000);
      return;
    }

    console.log('✅ Todos los datos necesarios están presentes');
  }, [cedula, documentoId, location.state, cedulaParam, navigate]);

  // Contador de tiempo para habilitar botón manual
  useEffect(() => {
    const timer = setInterval(() => {
      setTiempoTranscurrido(prev => {
        const nuevo = prev + 1;
        if (nuevo >= 10 && !capturada && !mostrarBotonManual) {
          setMostrarBotonManual(true);
          setMensaje('Si tienes dificultades, usa el botón "Tomar Foto" de abajo');
        }
        return nuevo;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [capturada, mostrarBotonManual]);

  useEffect(() => {
    const loadModels = async () => {
      const MODEL_URL = isDevelopment 
        ? (process.env.REACT_APP_NODE_ENV === 'production' ? process.env.REACT_APP_API_URL_PRODUCTION + '/models' : 'http://localhost:3001/models')
        : 'https://biofirma.colombianadetrasplantes.com/models';
      
      try {
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL)
        ]);
        setModelsLoaded(true);
        setMensaje('Cámara lista. Detectando rostro...');
        console.log('✅ Modelos de detección facial cargados exitosamente');
      } catch (error) {
        console.log('⚠️ Error cargando modelos:', error);
        setModelsLoaded(true);
        setMensaje('Cámara lista. Captura automática activada...');
      }
    };
    
    if (isDevelopment !== null) {
      loadModels();
    }
  }, [isDevelopment]);

  const calcularEAR = (eye) => {
    const dist = (p1, p2) => Math.hypot(p1.x - p2.x, p1.y - p2.y);
    const vertical1 = dist(eye[1], eye[5]);
    const vertical2 = dist(eye[2], eye[4]);
    const horizontal = dist(eye[0], eye[3]);
    return (vertical1 + vertical2) / (2.0 * horizontal);
  };

  const esParpadeo = (landmarks) => {
    const leftEAR = calcularEAR(landmarks.getLeftEye());
    const rightEAR = calcularEAR(landmarks.getRightEye());
    const EAR = (leftEAR + rightEAR) / 3.0;
    return EAR < 0.3;
  };

  const capturarFoto = () => {
    if (capturada || preparandoCaptura) return;
    
    setPreparandoCaptura(true);
    const foto = webcamRef.current?.getScreenshot();
    
    if (!foto) {
      console.error('❌ No se pudo capturar la foto desde webcam');
      setPreparandoCaptura(false);
      alert('⚠️ Error al capturar la foto. Por favor intenta de nuevo.');
      return;
    }

    console.log('📸 Foto capturada, procesando...');

    // Voltear la foto para corregir el efecto espejo
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.scale(-1, 1);
      ctx.drawImage(img, -img.width, 0);
      const fotoCorregida = canvas.toDataURL('image/png');
      
      setCapturada(true);
      setMensaje('Foto capturada. Continuando...');
      console.log('✅ Foto procesada correctamente');
      
      // 🚀 NAVEGACIÓN A FIRMA CON SALVAVIDAS (SALVAVIDAS #2)
      setTimeout(() => {
        try {
          console.log('🚀 ========== INICIANDO NAVEGACIÓN A FIRMA ==========');
          console.log('📋 Cédula:', cedula);
          console.log('📄 Documento ID:', documentoId);
          console.log('📸 Selfie capturada:', fotoCorregida ? 'SÍ' : 'NO');
          
          // Verificación crítica antes de navegar
          if (!documentoId) {
            console.error('❌ ERROR CRÍTICO: documentoId no está definido');
            alert('⚠️ Error: No se encontró el ID del documento. Por favor, recarga la página (F5) e intenta de nuevo.');
            setCapturada(false);
            setPreparandoCaptura(false);
            return;
          }

          if (!cedula) {
            console.error('❌ ERROR CRÍTICO: cedula no está definida');
            alert('⚠️ Error: No se encontró el número de cédula. Por favor, recarga la página (F5) e intenta de nuevo.');
            setCapturada(false);
            setPreparandoCaptura(false);
            return;
          }
          
          console.log('✅ Todos los datos verificados, navegando a /firmar/' + documentoId);
          
          // Intentar navegación con React Router
          navigate(`/firmar/${documentoId}`, {
            state: { 
              selfie: fotoCorregida, 
              cedula, 
              documentoId,
              firmanteIndex: firmanteIndex 
            },
            replace: false
          });
          
          console.log('✅ Comando de navegación ejecutado');
          
          // Verificación de que la navegación funcionó
          setTimeout(() => {
            if (window.location.pathname === `/selfie-capture/${cedula}`) {
              console.warn('⚠️ La navegación no funcionó, intentando método alternativo...');
              window.location.href = `/firmar/${documentoId}`;
            }
          }, 2000);
          
        } catch (error) {
          console.error('💥 ERROR en navegación:', error);
          alert('⚠️ Hubo un error al continuar. Detalle: ' + error.message + '\n\nIntentando método alternativo...');
          
          // Método alternativo: usar window.location
          console.log('🔄 Usando navegación alternativa con window.location');
          const firmanteParam = firmanteIndex !== null && firmanteIndex !== undefined ? `?firmante=${firmanteIndex}` : '';
          window.location.href = `/firmar/${documentoId}${firmanteParam}`;
        }
      }, 1500);
    };
    
    img.onerror = () => {
      console.error('❌ Error al procesar la imagen capturada');
      alert('⚠️ Error al procesar la foto. Por favor intenta de nuevo.');
      setCapturada(false);
      setPreparandoCaptura(false);
    };
    
    img.src = foto;
  };

  const capturarFotoManual = async () => {
    if (capturada || preparandoCaptura) return;
    
    console.log('👆 Usuario presionó botón manual de captura');
    
    // Si ya pasaron 10 segundos y el botón está visible, permitir captura más flexible
    if (mostrarBotonManual) {
      const video = webcamRef.current?.video;
      if (!video) {
        console.error('❌ No hay video disponible');
        return;
      }

      try {
        const detection = await faceapi
          .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
          .withFaceLandmarks();

        if (detection) {
          const { x, y, width, height } = detection.detection.box;
          const vw = video.videoWidth;
          const vh = video.videoHeight;

          const ovalCenterX = vw / 2;
          const ovalCenterY = vh / 2;
          const ovalWidth = vw * 0.45;
          const ovalHeight = vh * 0.6;

          const faceCenterX = x + width / 2;
          const faceCenterY = y + height / 2;

          const isInsideOval =
            Math.pow((faceCenterX - ovalCenterX) / (ovalWidth / 2), 2) +
            Math.pow((faceCenterY - ovalCenterY) / (ovalHeight / 2), 2) <= 1;

          if (isInsideOval) {
            console.log('✅ Rostro centrado, capturando...');
            setMensaje('Capturando tu foto...');
            setTimeout(() => {
              capturarFoto();
            }, 500);
          } else {
            console.warn('⚠️ Rostro fuera del óvalo');
            setMensaje('⚠️ Por favor, centra tu rostro dentro del óvalo azul antes de tomar la foto');
            if (navigator.vibrate) {
              navigator.vibrate(200);
            }
          }
        } else {
          console.warn('⚠️ No se detecta rostro');
          setMensaje('⚠️ No se detecta tu rostro. Asegúrate de estar frente a la cámara');
          if (navigator.vibrate) {
            navigator.vibrate(200);
          }
        }
      } catch (error) {
        // Si hay error en detección (modo desarrollo o problema técnico), permitir captura
        console.log('⚠️ Error en validación, capturando de todos modos (botón manual activo):', error);
        setMensaje('Capturando...');
        setTimeout(() => {
          capturarFoto();
        }, 500);
      }
    }
  };

  useEffect(() => {
    let interval;
    // IMPORTANTE: La detección automática se DETIENE después de 10 segundos
    if (modelsLoaded && webcamRef.current?.video && !mostrarBotonManual) {
      interval = setInterval(async () => {
        const video = webcamRef.current.video;
        
        try {
          const detection = await faceapi
            .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
            .withFaceLandmarks();

          if (detection && !capturada && !preparandoCaptura) {
            const { x, y, width, height } = detection.detection.box;
            const vw = video.videoWidth;
            const vh = video.videoHeight;

            const ovalCenterX = vw / 2;
            const ovalCenterY = vh / 2;
            const ovalWidth = vw * 0.45;
            const ovalHeight = vh * 0.6;

            const faceCenterX = x + width / 2;
            const faceCenterY = y + height / 2;

            const isInsideOval =
              Math.pow((faceCenterX - ovalCenterX) / (ovalWidth / 2), 2) +
              Math.pow((faceCenterY - ovalCenterY) / (ovalHeight / 2), 2) <= 1;

            if (isInsideOval) {
              // Actualizar estado visual
              setRostroEnOvalo(true);
              
              // SOLO capturar automáticamente si detecta parpadeo (en producción) o en desarrollo
              const shouldCapture = isDevelopment || esParpadeo(detection.landmarks);
              
              if (shouldCapture) {
                setMensaje(isDevelopment ? 'Capturando automáticamente...' : 'Parpadeo detectado. Capturando...');
                capturarFoto();
              } else {
                setMensaje('✓ Rostro centrado - Parpadea para capturar');
              }
            } else {
              setRostroEnOvalo(false);
              setMensaje('Alinea tu rostro dentro del óvalo');
            }
          } else if (!detection && !preparandoCaptura) {
            setRostroEnOvalo(false);
            setMensaje('Rostro no detectado. Intenta posicionarte mejor.');
          }
        } catch (error) {
          // Si hay error en detección, solo mostrar mensaje
          if (!preparandoCaptura) {
            setRostroEnOvalo(false);
            setMensaje('Error en detección. Intenta reposicionarte.');
          }
        }
      }, 900);
    } else if (mostrarBotonManual && !capturada) {
      // Después de 10 segundos, solo actualizar estado visual sin captura automática
      interval = setInterval(async () => {
        const video = webcamRef.current?.video;
        if (!video) return;
        
        try {
          const detection = await faceapi
            .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
            .withFaceLandmarks();

          if (detection) {
            const { x, y, width, height } = detection.detection.box;
            const vw = video.videoWidth;
            const vh = video.videoHeight;

            const ovalCenterX = vw / 2;
            const ovalCenterY = vh / 2;
            const ovalWidth = vw * 0.45;
            const ovalHeight = vh * 0.6;

            const faceCenterX = x + width / 2;
            const faceCenterY = y + height / 2;

            const isInsideOval =
              Math.pow((faceCenterX - ovalCenterX) / (ovalWidth / 2), 2) +
              Math.pow((faceCenterY - ovalCenterY) / (ovalHeight / 2), 2) <= 1;

            setRostroEnOvalo(isInsideOval);
          } else {
            setRostroEnOvalo(false);
          }
        } catch (error) {
          setRostroEnOvalo(false);
        }
      }, 500);
    }
    return () => clearInterval(interval);
  }, [modelsLoaded, capturada, preparandoCaptura, cedula, documentoId, navigate, isDevelopment, mostrarBotonManual]);

  // Mostrar mensaje si faltan datos críticos
  if (!cedula || !documentoId) {
    return (
      <div style={{ maxWidth: '500px', margin: 'auto', padding: '20px', textAlign: 'center' }}>
        <div style={{ 
          backgroundColor: '#dc3545', 
          color: 'white', 
          padding: '20px', 
          borderRadius: '10px',
          marginTop: '50px'
        }}>
          <h3>⚠️ Error</h3>
          <p>No se pudieron cargar los datos necesarios.</p>
          <p>Serás redirigido al inicio...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '500px', margin: 'auto', padding: '20px', position: 'relative' }}>
      <div className="d-flex justify-content-center align-items-center mb-4">
        <img src="/logo2.png" alt="Logo Empresa" style={{ width: '150px', marginRight: '20px' }} />
        <img src="/logo1.png" alt="Logo Proyecto" style={{ width: '80px' }} />
      </div>

      <h3 className="text-center mb-3">Captura tu Selfie</h3>

      {/* Contador visual para usuarios */}
      {tiempoTranscurrido > 0 && !capturada && !mostrarBotonManual && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          backgroundColor: '#007bff',
          color: 'white',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '16px',
          fontWeight: 'bold',
          zIndex: 10
        }}>
          {Math.max(0, 10 - tiempoTranscurrido)}
        </div>
      )}

      <div style={{ position: 'relative', width: '100%' }}>
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/png"
          videoConstraints={{ facingMode: 'user' }}
          style={{ 
            width: '100%', 
            borderRadius: '10px',
            transform: 'scaleX(-1)',
            opacity: capturada ? 0.7 : 1
          }}
        />
        
        {/* Overlay del óvalo con indicador visual de posición */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '45%',
          height: '60%',
          border: `3px dashed ${capturada ? '#28a745' : rostroEnOvalo ? '#00ff00' : '#007bff'}`,
          borderRadius: '50%',
          pointerEvents: 'none',
          backgroundColor: rostroEnOvalo && !capturada ? 'rgba(0, 255, 0, 0.1)' : 'transparent',
          transition: 'all 0.3s ease'
        }} />
        
        {/* Texto guía */}
        <div style={{
          position: 'absolute',
          top: '78%',
          left: '50%',
          transform: 'translateX(-50%)',
          color: capturada ? '#28a745' : '#007bff',
          fontSize: '14px',
          fontWeight: 'bold',
          backgroundColor: 'white',
          padding: '2px 8px',
          borderRadius: '20px',
          opacity: 0.9
        }}>
          {capturada ? '✓ Foto capturada' : 'Coloca tu rostro aquí'}
        </div>

        {/* Indicador de captura */}
        {preparandoCaptura && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '10px'
          }}>
            <div style={{
              backgroundColor: '#28a745',
              color: 'white',
              padding: '20px',
              borderRadius: '10px',
              fontSize: '18px',
              fontWeight: 'bold'
            }}>
              Capturando...
            </div>
          </div>
        )}
      </div>

      <p className="text-center mt-3" style={{ 
        color: mensaje.includes('Capturando') || capturada ? 'green' : 
               mensaje.includes('✓') ? '#00cc00' :
               mensaje.includes('Parpadea') ? 'blue' : 
               mensaje.includes('Alinea') ? 'orange' : 
               mensaje.includes('⚠️') ? '#dc3545' :
               mensaje.includes('dificultades') ? '#6f42c1' : 'red',
        fontWeight: 'bold',
        fontSize: '16px',
        minHeight: '24px',
        textShadow: mensaje.includes('⚠️') ? '0 0 10px rgba(220, 53, 69, 0.3)' : 'none'
      }}>
        {mensaje}
      </p>

      {/* Botón flotante para captura manual */}
      {mostrarBotonManual && !capturada && (
        <button
          onClick={capturarFotoManual}
          disabled={preparandoCaptura || !rostroEnOvalo}
          style={{
            position: 'fixed',
            bottom: '30px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: rostroEnOvalo ? '#28a745' : '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '50px',
            padding: '15px 25px',
            fontSize: '18px',
            fontWeight: 'bold',
            boxShadow: rostroEnOvalo ? '0 4px 12px rgba(40, 167, 69, 0.4)' : '0 4px 12px rgba(0,0,0,0.3)',
            cursor: (preparandoCaptura || !rostroEnOvalo) ? 'not-allowed' : 'pointer',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            transition: 'all 0.3s ease',
            opacity: rostroEnOvalo ? 1 : 0.6
          }}
          onMouseOver={(e) => {
            if (rostroEnOvalo && !preparandoCaptura) {
              e.target.style.transform = 'translateX(-50%) scale(1.05)';
            }
          }}
          onMouseOut={(e) => {
            e.target.style.transform = 'translateX(-50%) scale(1)';
          }}
        >
          Tomar Foto
          {preparandoCaptura && (
            <div style={{
              width: '20px',
              height: '20px',
              border: '2px solid white',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          )}
        </button>
      )}

      <div style={{
        marginTop: '20px',
        padding: '15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <h4 style={{ fontSize: '16px', marginBottom: '10px', color: '#495057' }}>
          Instrucciones:
        </h4>
        <ul style={{
          textAlign: 'left',
          fontSize: '14px',
          color: '#666',
          margin: 0,
          paddingLeft: '20px'
        }}>
          <li style={{ marginBottom: '5px' }}>Centra tu rostro dentro del óvalo azul</li>
          <li style={{ marginBottom: '5px' }}>Mantén una distancia de 60-80 cm de la cámara</li>
          <li style={{ marginBottom: '5px' }}>Asegúrate de tener buena iluminación</li>
          <li style={{ marginBottom: '5px' }}>
            {isDevelopment 
              ? 'La foto se tomará automáticamente (modo desarrollo)'
              : 'La foto se tomará automáticamente al detectar un parpadeo'
            }
          </li>
          <li>
            Si tienes dificultades, después de 10 segundos aparecerá un botón para tomar la foto manualmente
          </li>
        </ul>
        
        {isDevelopment && (
          <div style={{
            marginTop: '10px',
            padding: '8px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#856404'
          }}>
            Modo desarrollo activado - Captura automática
          </div>
        )}

        {mostrarBotonManual && (
          <div style={{
            marginTop: '10px',
            padding: '8px',
            backgroundColor: '#e7f3ff',
            border: '1px solid #b3d7ff',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#0066cc'
          }}>
            Botón manual activado - Puedes tomar la foto cuando quieras
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default SelfieCapturePage;